import "./GroupCardShared.scss";

import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { PreviewUserNames } from "components/materials/page/groups/PreviewUserNames";
import { getUserActivityIcon } from "components/materials/section/helpers";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { CircleInfoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  pageGroupSelectors,
  sectionUserSelectors,
  selectActivityStatusForUser,
  selectPreviewUserIdsForPageGroup,
  selectSortedPageGroupUsersByPageGroupId,
  selectUserBySectionUserId,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { compactMap } from "utils/collections";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

interface PageGroupInfoCardUserProps {
  sectionUserId: string;
}

const PageGroupInfoCardUser: FC<PageGroupInfoCardUserProps> = ({ sectionUserId }) => {
  const user = useObjectSelector(
    (s) => selectUserBySectionUserId(s, sectionUserId),
    PROFILE_PROPERTIES,
  );
  const activityStatus = useAppSelector((s) => selectActivityStatusForUser(s, user?.id));

  return (
    <div className="flex items-center gap-1.5">
      {getUserActivityIcon(activityStatus, "xs")}
      <span className="truncate text-xs">{getUserFullName(user)}</span>
    </div>
  );
};

interface PageGroupInfoCardProps {
  className?: string;
  pageGroupId?: string | null;
  pageGroupIndex: number;
  children?: ReactNode;
  isInstructorView?: boolean;
}

export const PageGroupInfoCard: FC<PageGroupInfoCardProps> = ({
  className,
  pageGroupId,
  pageGroupIndex,
  children,
  isInstructorView = false,
}) => {
  const pageGroup = useObjectSelector(
    (s) => pageGroupSelectors.selectById(s, pageGroupId),
    ["auto_created", "page_id"],
  );
  const pageGroupUsers = useListSelector(
    (s) => selectSortedPageGroupUsersByPageGroupId(s, pageGroupId),
    ["id", "section_user_id"],
  );
  const memberSectionUsers = useListSelector(
    (state) =>
      compactMap(pageGroupUsers, (pageGroupUser) =>
        sectionUserSelectors.selectById(state, pageGroupUser.section_user_id),
      ),
    ["user_id"],
  );

  const previewUserIdsForGroup = useListSelector((state) =>
    selectPreviewUserIdsForPageGroup(state, pageGroup?.page_id ?? "", pageGroupId ?? ""),
  );

  if (!pageGroup || !pageGroupId) return null;

  const memberUserIds = new Set(memberSectionUsers.map((sectionUser) => sectionUser.user_id));
  const previewUserIds = previewUserIdsForGroup.filter((userId) => !memberUserIds.has(userId));

  return (
    <div
      className={twMerge(
        "group-card flex items-start justify-between gap-3 rounded-[12px] border border-hibiscus-tint-70 bg-hibiscus-tint-93 px-[18px] py-4 supports-squircle:rounded-[24px] supports-squircle:corner-squircle",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="body-s text-hibiscus">
            {t("common.group_title", { number: pageGroupIndex + 1 })}
          </span>

          <span className="body-xs flex items-center gap-2 text-black-tint-40">
            <span aria-hidden="true">·</span>
            <span>{t("page_group_info_card.member_count", { count: pageGroupUsers.length })}</span>
          </span>

          {pageGroup.auto_created && (
            <Tooltip
              content={t(
                isInstructorView
                  ? "tooltip.auto_created_group"
                  : "tooltip.auto_created_group_student",
              )}
            >
              <CircleInfoIcon className="text-orange" size="s" />
            </Tooltip>
          )}
        </div>

        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-4">
            {pageGroupUsers.map((pageGroupUser) => (
              <PageGroupInfoCardUser
                key={pageGroupUser.id}
                sectionUserId={pageGroupUser.section_user_id}
              />
            ))}
          </div>

          <PreviewUserNames userIds={previewUserIds} />

          {pageGroupUsers.length === 0 && previewUserIds.length === 0 && (
            <div className="body-xs flex items-center text-black-tint-55">
              {t("page_group_info_card.no_one_in_group")}
            </div>
          )}
        </div>
      </div>

      {children}
    </div>
  );
};
